import type { FC } from 'react';

import { Stack } from '@eneco/sparky';

import { TextInputField } from './TextInputField';
import type { FormFieldDescriptor } from './types';
import { fieldRule, readFormField } from './useFormField';

/** One sub-field of a composite: its role in the family array and the name to fall back to. */
export interface FieldPart {
  role: string;
  fallbackName: string;
  type?: 'text' | 'number';
}

/**
 * Body shared by the composite fields — name, address, date — each of which is several descriptors
 * in one `genericFields` array rather than a single one.
 */
export const MultiPartField: FC<{ parts: readonly FieldPart[]; direction?: 'row' | 'column' }> = ({ parts, direction = 'row' }) => (
  <Stack direction={{ initial: 'column', md: direction }} gap="4">
    {parts.map(part => (
      <Stack.Item key={part.role} grow>
        <TextInputField role={part.role} fallbackName={part.fallbackName} type={part.type ?? 'text'} />
      </Stack.Item>
    ))}
  </Stack>
);

/** Every part's rule, merged — so a composite validates each sub-field on its own terms. */
export const multiPartSchema = (content: Record<string, unknown>, parts: readonly FieldPart[]) =>
  parts.reduce<Record<string, ReturnType<typeof fieldRule>[string]>>(
    (rules, part) => Object.assign(rules, fieldRule(readFormField(content, 'genericFields', part.role) as FormFieldDescriptor, part.fallbackName)),
    {},
  );
